import React, { useCallback } from 'react';

import { di } from 'react-magnetic-di';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { type ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { BrowseAgentsModal } from '@atlassian/conversation-assistant-agent/ui/BrowseAgentsSidebarModal';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	useUIStoreActions,
	useUIStoreSubscription,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';

import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useAgentsModal } from '../../controllers/agents-modal/useAgentsModal';
import { useChatStoreActions } from '../../controllers/chat-store/useChatStoreActions';

/* Gotcha: Since the introduction of the chat framework refresh (rovo_chat_framework_refresh), the browse agents view
loads into the Rovo Chat panel, rather than a modal. However, we have a rovoParam that can trigger the opening
of the agent modal via a URL, so we must keep this here until we decide to redirect that action to the panel experience,
or decide to remove the modal entirely. */
export const AgentModal = (): React.JSX.Element => {
	di(useAgentsModal, fg);
	const [{ isOpen }, { closeAgentModal }] = useAgentsModal();
	const { sendAnalyticsEvent } = useAnalytics();
	useUIStoreSubscription();
	const { setActiveMenu } = useUIStoreActions();
	const { fullScreen } = useUIConfig();

	const assistanceServiceParams = useAssistanceServiceParams({
		touchpointSource: 'agentModal',
	});
	const { createConversationOptimistically, sendMessage } = useChatStoreActions();

	const {
		preferences: { storeUserMessages },
	} = useUserPreferences();

	const handleClickChat = useCallback(
		(agent: Agent) => {
			createConversationOptimistically({
				sourceAgentId: agent.id,
			});
			if (!fullScreen) {
				setActiveMenu('none');
			}
			closeAgentModal();
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'agentModal',
				actionSubjectId: 'agentCard',
			});
		},
		[
			createConversationOptimistically,
			fullScreen,
			closeAgentModal,
			sendAnalyticsEvent,
			setActiveMenu,
		],
	);

	const handleClickConversationStarter = useCallback(
		(agent: Agent, conversationStarter: ConversationStarter) => {
			createConversationOptimistically({
				sourceAgentId: agent.id,
			});
			sendMessage({
				agent,
				prompt: encodeAdf(conversationStarter.message),
				// TODO(ai_mate_store_messages_be_driven): remove this local storeUserMessages payload
				// value when BE user settings are fully rolled out; BE overrides it via the capability header.
				storeUserMessages,
				messageSources: {
					interactionSource: 'viewAgentConversationStarters',
					followUpSource: 'ai-mate',
				},
			});
		},
		[createConversationOptimistically, sendMessage, storeUserMessages],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<BrowseAgentsModal
					onClose={closeAgentModal}
					siteId={assistanceServiceParams.siteId}
					onClickChat={handleClickChat}
					handleClickConversationStarter={handleClickConversationStarter}
					assistanceServiceParams={assistanceServiceParams}
				/>
			)}
		</ModalTransition>
	);
};
